import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  MoreVert,
  Search,
  Delete,
  ContentCopy,
  PowerSettingsNew,
  PowerOff,
} from "@mui/icons-material";
import {
  useReadManySubscriptionsQuery,
  useUpdateSubscriptionStatusMutation,
  useDeleteSubscriptionMutation,
} from "api/endpoints/subscription";
import {
  getPlanDisplayName,
  getStatusDisplayName,
  getStatusColor,
  SubscriptionPlanType,
  SubscriptionStatus,
} from "@dots-crm/contracts/client";
import { ConfirmationDialogComponent } from "lib/components/ui/confirmation-dialog";

// Removed - using centralized functions from common/contracts

export const SubscriptionListComponent = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<string>("");
  const [selectedSubscriptionUrl, setSelectedSubscriptionUrl] =
    useState<string>("");
  
  // Confirmation dialog states
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    confirmColor: "primary" | "secondary" | "error" | "success" | "warning" | "info";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "تایید",
    confirmColor: "primary",
    onConfirm: () => {},
  });

  const {
    data: subscriptionsList,
    isLoading,
    error,
    refetch,
  } = useReadManySubscriptionsQuery({
    query: {
      page: String(page + 1),
      limit: String(rowsPerPage),
      search: search || undefined,
      status:
        (statusFilter as "active" | "disabled" | "waiting-for-review") ||
        undefined,
    },
  });

  const [updateStatus] = useUpdateSubscriptionStatusMutation();
  const [deleteSubscription] = useDeleteSubscriptionMutation();

  const subscriptions = subscriptionsList?.subscriptions || [];
  const totalCount = subscriptionsList?.count || 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    subscriptionId: string,
    subscriptionUrl: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedSubscription(subscriptionId);
    setSelectedSubscriptionUrl(subscriptionUrl);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedSubscription("");
    setSelectedSubscriptionUrl("");
  };

  const showConfirmationDialog = (
    title: string,
    message: string,
    confirmText: string,
    confirmColor: "primary" | "secondary" | "error" | "success" | "warning" | "info",
    onConfirm: () => void
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      confirmColor,
      onConfirm,
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleStatusUpdate = (status: "active" | "disabled") => {
    if (!selectedSubscription) return;

    const actionText = status === "active" ? "فعال کردن" : "غیرفعال کردن";
    
    showConfirmationDialog(
      `${actionText} اشتراک`,
      `آیا از ${actionText} این اشتراک اطمینان دارید؟`,
      actionText,
      status === "active" ? "success" : "error",
      async () => {
        try {
          await updateStatus({
            params: { subscriptionId: selectedSubscription },
            payload: { status },
          }).unwrap();

          refetch();
          handleMenuClose();
          closeConfirmationDialog();
        } catch (error) {
          console.error("Failed to update status:", error);
          closeConfirmationDialog();
        }
      }
    );
  };

  const handleDelete = () => {
    if (!selectedSubscription) return;

    showConfirmationDialog(
      "حذف اشتراک",
      "آیا از حذف این اشتراک اطمینان دارید؟ این عمل قابل بازگشت نیست.",
      "حذف",
      "error",
      async () => {
        try {
          await deleteSubscription({
            params: { subscriptionId: selectedSubscription },
          }).unwrap();

          refetch();
          handleMenuClose();
          closeConfirmationDialog();
        } catch (error) {
          console.error("Failed to delete subscription:", error);
          closeConfirmationDialog();
        }
      }
    );
  };

  const handleCopyLink = async () => {
    if (!selectedSubscriptionUrl) return;

    try {
      await navigator.clipboard.writeText(selectedSubscriptionUrl);
      // You could add a toast notification here
      handleMenuClose();
    } catch (error) {
      console.error("Failed to copy link:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = selectedSubscriptionUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      handleMenuClose();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        خطا در بارگذاری لیست اشتراک‌ها
      </Alert>
    );
  }
  console.log(subscriptions);
  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        مدیریت اشتراک‌ها
      </Typography>

      <Paper sx={{ p: 3 }}>
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="جستجو در نام یا شماره تلفن..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>وضعیت</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="وضعیت"
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="active">فعال</MenuItem>
                <MenuItem value="disabled">غیرفعال</MenuItem>
                <MenuItem value="waiting-for-review">در انتظار بررسی</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Enhanced Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>کاربر</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>شماره تلفن</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>نوع پلن</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>حجم/زمان</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>قیمت</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>وضعیت</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>تاریخ ایجاد</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'center' }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((subscription) => (
                <TableRow 
                  key={subscription._id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'grey.25',
                    },
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                >
                  <TableCell>{subscription.userFullName}</TableCell>
                  <TableCell dir="ltr">
                    {subscription.userPhoneNumber}
                  </TableCell>
                  <TableCell>
                    {getPlanDisplayName(
                      subscription.planType as SubscriptionPlanType
                    )}
                  </TableCell>
                  <TableCell>
                    {(subscription.gigLimit && `${subscription.gigLimit} GB`) ||
                      ""}
                    {(subscription.gigLimit &&
                      subscription.timeLimit &&
                      " / ") ||
                      ""}
                    {(subscription.timeLimit &&
                      `${subscription.timeLimit} ماه`) ||
                      ""}
                    {!subscription.gigLimit &&
                      !subscription.timeLimit &&
                      "نامحدود"}
                  </TableCell>
                  <TableCell>
                    {subscription.price.toLocaleString()} تومان
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusDisplayName(
                        subscription.status as SubscriptionStatus
                      )}
                      color={
                        getStatusColor(
                          subscription.status as SubscriptionStatus
                        ) as any
                      }
                      size="small"
                      sx={{
                        fontWeight: 500,
                        borderRadius: 2,
                        '& .MuiChip-label': {
                          px: 1.5,
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(subscription.createdAt).toLocaleDateString(
                      "fa-IR"
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={(e) =>
                        handleMenuOpen(
                          e,
                          subscription._id,
                          (subscription as any).subscriptionUrl || ""
                        )
                      }
                      sx={{
                        backgroundColor: 'action.hover',
                        '&:hover': {
                          backgroundColor: 'primary.100',
                          color: 'primary.main',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease-in-out',
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} از ${count !== -1 ? count : to}`
          }
        />

        {/* Enhanced Action Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(0,0,0,0.08)',
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Copy Link Section */}
          {selectedSubscriptionUrl && (
            <>
              <MenuItem 
                onClick={handleCopyLink}
                sx={{ 
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'primary.50',
                  }
                }}
              >
                <ListItemIcon>
                  <ContentCopy color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="کپی لینک اشتراک"
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
              <Divider />
            </>
          )}

          {/* Status Management Section */}
          <MenuItem 
            onClick={() => handleStatusUpdate("active")}
            sx={{ 
              py: 1.5,
              '&:hover': {
                backgroundColor: 'success.50',
              }
            }}
          >
            <ListItemIcon>
              <PowerSettingsNew color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="فعال کردن اشتراک"
              secondary="اشتراک را فعال کنید"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'success.main',
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            />
          </MenuItem>

          <MenuItem 
            onClick={() => handleStatusUpdate("disabled")}
            sx={{ 
              py: 1.5,
              '&:hover': {
                backgroundColor: 'error.50',
              }
            }}
          >
            <ListItemIcon>
              <PowerOff color="error" />
            </ListItemIcon>
            <ListItemText 
              primary="غیرفعال کردن اشتراک"
              secondary="اشتراک را غیرفعال کنید"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'error.main',
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            />
          </MenuItem>

          <Divider />

          {/* Danger Zone */}
          <MenuItem 
            onClick={handleDelete}
            sx={{ 
              py: 1.5,
              '&:hover': {
                backgroundColor: 'error.50',
              }
            }}
          >
            <ListItemIcon>
              <Delete color="error" />
            </ListItemIcon>
            <ListItemText 
              primary="حذف اشتراک"
              secondary="این عمل قابل بازگشت نیست"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'error.main',
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: 'error.light',
              }}
            />
          </MenuItem>
        </Menu>

        {/* Confirmation Dialog */}
        <ConfirmationDialogComponent
          isOpen={confirmationDialog.isOpen}
          onClose={closeConfirmationDialog}
          onConfirm={confirmationDialog.onConfirm}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          confirmText={confirmationDialog.confirmText}
          confirmColor={confirmationDialog.confirmColor}
        />
      </Paper>
    </Box>
  );
};
